import { Controller } from "@hotwired/stimulus"
import ApexCharts from "apexcharts"
import { monthAbbrToFull, isDark, compactBRL } from "../../lib/utils"

const rollbackSVG = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 256 256"><rect width="256" height="256" fill="none"/><polyline points="24 56 24 104 72 104" fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="16"/><path d="M67.59,192A88,88,0,1,0,65.77,65.77L24,104" fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="16"/></svg>'

export default class extends Controller {
  static values = {
    data: Array,
    label: String,
    debit: Boolean
  }

  connect() {
    // Don't render chart if there's no data
    if (!this.hasRenderableData()) {
      this.element.innerHTML = `
        <div class="flex items-center justify-center h-full text-base-content/60 text-sm">
          Sem dados para exibir
        </div>
      `
      return
    }

    const categories = this.dataValue.map(d => d.ref_at)
    const hasMonth = d => (d.revenue_cents || 0) !== 0 || (d.expense_cents || 0) !== 0 || (d.amount_cents || 0) !== 0
    // Receitas and Despesas as grouped columns (both up). Empty months stay null so no bar is drawn.
    const revenue = this.dataValue.map(d => hasMonth(d) ? (d.revenue_cents || 0) : null)
    const expense = this.dataValue.map(d => hasMonth(d) ? Math.abs(d.expense_cents || 0) : null)

    this.chart = new ApexCharts(this.element, {
      chart: {
        type: 'bar',
        height: '100%',
        width: '100%',
        zoom: { enabled: false },
        toolbar: { show: false, tools: { reset: rollbackSVG } },
        fontFamily: 'inherit',
      },
      series: [
        { name: 'Receitas', type: 'column', data: revenue },
        { name: 'Despesas', type: 'column', data: expense },
      ],
      plotOptions: {
        bar: {
          columnWidth: '70%',
          borderRadius: 4,
          borderRadiusApplication: 'end',
        }
      },
      xaxis: {
        categories: categories,
        axisBorder: { show: false },
        axisTicks: { show: false },
        labels: {
          style: {
            colors: 'var(--color-base-content)',
          }
        }
      },
      yaxis: {
        labels: {
          formatter: (value) => compactBRL(value / 100),
          style: {
            colors: 'var(--color-base-content)',
          }
        }
      },
      colors: ['var(--color-success)', 'var(--color-error)'],
      dataLabels: { enabled: false },
      legend: {
        show: true,
        position: 'top',
        horizontalAlign: 'right',
        fontSize: '12px',
        markers: { size: 8 },
        labels: {
          colors: 'var(--color-base-content)',
        },
      },
      tooltip: {
        shared: true,
        intersect: false,
        custom: ({ dataPointIndex }) => {
          const d = this.dataValue[dataPointIndex]
          const label = monthAbbrToFull[categories[dataPointIndex]] || categories[dataPointIndex]
          const netColor = d.amount_cents >= 0 ? 'var(--color-success)' : 'var(--color-error)'

          return `<div class="py-2 text-xs bg-base-100 border text-base-content rounded-box shadow-2xl min-w-40">
            <div class="font-semibold mb-2 border-b px-4 pb-2">${label}</div>
            <div class="flex items-center justify-between gap-4 px-4">
              <span class="text-success">Receitas</span>
              <span class="font-medium">${d.revenue}</span>
            </div>
            <div class="flex items-center justify-between gap-4 px-4">
              <span class="text-error">Despesas</span>
              <span class="font-medium">${d.expense}</span>
            </div>
            <div class="flex items-center justify-between gap-4 px-4 mt-1 border-t pt-1">
              <span>Saldo líquido</span>
              <span class="font-semibold" style="color:${netColor}">${d.amount}</span>
            </div>
          </div>`
        }
      },
      grid: {
        borderColor: isDark() ? '#374151' : '#d1d5db',
        strokeDashArray: 4,
      }
    })

    this.chart.render()
    window.addEventListener("theme:change", this.updateChartForTheme)
  }

  updateChartForTheme = () => {
    if (!this.chart) return

    this.chart.updateOptions({
      grid: {
        borderColor: isDark() ? '#374151' : '#d1d5db',
      }
    })
  }

  hasRenderableData() {
    return this.dataValue.some((dataPoint) => {
      return [dataPoint.amount_cents, dataPoint.revenue_cents, dataPoint.expense_cents].some((value) => {
        const cents = Number(value)
        return Number.isFinite(cents) && cents !== 0
      })
    })
  }

  disconnect() {
    window.removeEventListener("theme:change", this.updateChartForTheme)

    if (this.chart) {
      this.chart.destroy()
      this.chart = null
    }
  }
}
